import { useCallback, useEffect, useMemo, useRef, useState, type FormEvent } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import Section from '@/components/Section'
import PageHero from '@/components/PageHero'
import { usePageTitle } from '@/components/usePageTitle'
import { search } from '@/lib/api'
import {
  PAGE_SIZE,
  formatCount,
  formatDate,
  isSearchable,
  pageWindow,
  readSearchUrl,
  sanitizeHexInput,
  sanitizeNameInput,
  writeSearchUrl,
  type SearchResult,
  type SearchType,
  type SearchUrlState,
  type SortColumn,
} from '@/lib/registry'

const EMPTY: SearchResult = { search: false, query: '', type: 'name', order: 'relevant', limit: PAGE_SIZE, offset: 0, count: 0, capped: false, timer: 0, results: [] }

function SortCaret({ active, dir }: { active: boolean; dir: 'asc' | 'desc' }) {
  if (!active) return null
  return (
    <svg viewBox="0 0 16 16" className="ml-1 inline h-3 w-3 fill-current" aria-hidden="true">
      {dir === 'asc' ? <path d="M7.247 4.86l-4.796 5.481c-.566.647-.106 1.659.753 1.659h9.592a1 1 0 0 0 .753-1.659l-4.796-5.48a1 1 0 0 0-1.506 0z" /> : <path d="M7.247 11.14L2.451 5.658C1.885 5.013 2.345 4 3.204 4h9.592a1 1 0 0 1 .753 1.659l-4.796 5.48a1 1 0 0 1-1.506 0z" />}
    </svg>
  )
}

/**
 * Registry search. The search term, type, sort, and page live in the URL
 * (?type=hex&q=%23ff&sort=name-desc&page=3), so any search can be shared and
 * the back button steps through sorts and pages. Results stay on screen while
 * the next search runs, so typing never makes the table blink.
 */
export default function Registry() {
  usePageTitle('Registry', 'Search the ColorMark registry by color name or hex code.')
  const [params, setParams] = useSearchParams()
  const state = useMemo(() => readSearchUrl(params), [params])
  const { type, query, order, page } = state
  const offset = (page - 1) * PAGE_SIZE

  const update = useCallback(
    (patch: Partial<SearchUrlState>, replace = false) => {
      setParams(writeSearchUrl({ ...state, ...patch }), { replace })
    },
    [setParams, state],
  )

  const [menuOpen, setMenuOpen] = useState(false)
  const menuRef = useRef<HTMLDivElement>(null)
  useEffect(() => {
    function onDoc(e: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) setMenuOpen(false)
    }
    document.addEventListener('mousedown', onDoc)
    return () => document.removeEventListener('mousedown', onDoc)
  }, [])

  const active = isSearchable(query, type)
  const reqKey = `${type}|${query}|${order}|${offset}`
  // The last answer, kept on screen (even for an older key) until the next one arrives.
  const [fetched, setFetched] = useState<{ key: string; result: SearchResult } | null>(null)
  const result = active && fetched ? fetched.result : EMPTY
  const loading = active && fetched?.key !== reqKey

  // Debounced search against /api/search (falls back to bundled data offline).
  useEffect(() => {
    if (!active) return
    let alive = true
    const t = setTimeout(() => {
      search({ query, type, order, limit: PAGE_SIZE, offset }).then((r) => {
        if (alive) setFetched({ key: reqKey, result: r })
      })
    }, 150)
    return () => {
      alive = false
      clearTimeout(t)
    }
  }, [query, type, order, offset, active, reqKey])

  const pages = Math.max(1, Math.ceil(result.count / PAGE_SIZE))
  const pageLinks = pageWindow(page, pages, 5)

  function switchType(next: SearchType) {
    setFetched(null)
    setMenuOpen(false)
    update({ type: next, query: '', order: 'relevant', page: 1 })
  }

  function onInput(raw: string) {
    const next = type === 'hex' ? sanitizeHexInput(raw) : sanitizeNameInput(raw)
    if (!isSearchable(next, type)) setFetched(null)
    update({ query: next, page: 1 }, true)
  }

  function sortBy(col: SortColumn) {
    const [curCol, curDir] = order === 'relevant' ? [null, null] : (order.split('-') as [SortColumn, 'asc' | 'desc'])
    const dir = curCol === col && curDir === 'asc' ? 'desc' : 'asc'
    update({ order: `${col}-${dir}`, page: 1 })
  }

  function goTo(p: number) {
    update({ page: Math.min(Math.max(1, p), pages) })
  }

  function onSubmit(e: FormEvent) {
    e.preventDefault()
    ;(e.currentTarget as HTMLFormElement).querySelector('input')?.blur()
  }

  const [sortCol, sortDir] = order === 'relevant' ? [null, 'asc' as const] : (order.split('-') as [SortColumn, 'asc' | 'desc'])

  const th = 'cursor-pointer select-none border-b-2 border-neutral-300 px-3 py-3 text-left font-medium hover:underline'
  const pageBtn = 'cursor-pointer border border-neutral-300 px-3 py-2 disabled:cursor-default disabled:opacity-40'

  return (
    <>
      <PageHero image="/images/color-books-bg.webp" overlay={0.2} title="Search Registry" align="left" />

      <Section>
        <div className="mx-auto max-w-[1800px] font-body">
          <form onSubmit={onSubmit} role="search" className="flex w-full items-stretch">
            <div ref={menuRef} className="relative">
              <button
                type="button"
                onClick={() => setMenuOpen((v) => !v)}
                aria-haspopup="listbox"
                aria-expanded={menuOpen}
                className="h-full cursor-pointer rounded-l border border-neutral-500 px-4 text-neutral-600 hover:bg-neutral-600 hover:text-white"
              >
                {type === 'hex' ? 'Hex' : 'Name'}
                <span className="ml-2 inline-block border-x-[0.3em] border-t-[0.3em] border-x-transparent border-t-current align-middle" aria-hidden="true" />
              </button>
              {menuOpen && (
                <div className="absolute left-0 top-full z-10 mt-1 min-w-40 rounded border border-black/15 bg-white py-2 shadow" role="listbox">
                  {(['name', 'hex'] as SearchType[]).map((t) => (
                    <button
                      key={t}
                      type="button"
                      role="option"
                      aria-selected={type === t}
                      onClick={() => switchType(t)}
                      className="block w-full cursor-pointer px-6 py-1 text-left hover:bg-neutral-100"
                    >
                      {t === 'hex' ? 'Hex' : 'Name'}
                    </button>
                  ))}
                </div>
              )}
            </div>
            <input
              type="search"
              value={query}
              onChange={(e) => onInput(e.target.value)}
              placeholder={type === 'hex' ? 'Search Color Codes' : 'Search Color Names'}
              maxLength={type === 'hex' ? 7 : 100}
              spellCheck={false}
              autoComplete="off"
              aria-label={type === 'hex' ? 'Search color codes' : 'Search color names'}
              className="min-w-0 flex-1 border border-x-0 border-neutral-500 px-3 py-2 text-lg outline-none focus:ring-2 focus:ring-blue-300"
            />
            <button type="submit" aria-label="Search" className="cursor-pointer rounded-r bg-neutral-600 px-4 text-white hover:bg-neutral-700">
              <svg viewBox="0 0 16 16" className="h-5 w-5 fill-current" aria-hidden="true">
                <path d="M11.742 10.344a6.5 6.5 0 1 0-1.397 1.398h-.001c.03.04.062.078.098.115l3.85 3.85a1 1 0 0 0 1.415-1.414l-3.85-3.85a1.007 1.007 0 0 0-.115-.1zM12 6.5a5.5 5.5 0 1 1-11 0 5.5 5.5 0 0 1 11 0z" />
              </svg>
            </button>
          </form>

          {/* Fixed-height status line so "Searching…" never shifts the page. */}
          <p className="mt-3 h-6 text-sm italic leading-6 text-neutral-500" aria-live="polite">
            {loading ? 'Searching…' : ''}
          </p>

          {active && result.search && (
            <>
              <div className="mt-4 overflow-x-auto" aria-busy={loading || undefined}>
                <table className="w-full border-collapse text-[1rem]">
                  <thead>
                    <tr>
                      <th className="w-[10%] border-b-2 border-neutral-300 px-3 py-3 text-left font-medium">Color</th>
                      <th className={`${th} w-[10%]`} onClick={() => sortBy('hex')}>
                        Code <SortCaret active={sortCol === 'hex'} dir={sortDir} />
                      </th>
                      <th className={`${th} w-[50%]`} onClick={() => sortBy('name')}>
                        Name <SortCaret active={sortCol === 'name'} dir={sortDir} />
                      </th>
                      <th className={`${th} hidden w-[15%] sm:table-cell`} onClick={() => sortBy('reg')}>
                        Registration <SortCaret active={sortCol === 'reg'} dir={sortDir} />
                      </th>
                      <th className={`${th} hidden w-[15%] sm:table-cell`} onClick={() => sortBy('exp')}>
                        Expiration <SortCaret active={sortCol === 'exp'} dir={sortDir} />
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {result.results.map((c) => {
                      const reserved = c.type === 'reserved'
                      return (
                        <tr key={c.slug} className="border-t border-neutral-200">
                          <td className="px-3 py-3">
                            <div className="h-6 w-16 border border-black/10" style={{ backgroundColor: c.hex }} />
                          </td>
                          <td className="px-3 py-3">
                            <Link to={`/color/${c.hex.slice(1)}`} className="text-[#007bff] no-underline hover:underline">
                              {c.hex}
                            </Link>
                          </td>
                          <td className="px-3 py-3">
                            <Link to={`/color/${c.slug}`} className="text-[#007bff] no-underline hover:underline">
                              {c.name}
                            </Link>
                          </td>
                          <td className="hidden px-3 py-3 sm:table-cell">{formatDate(c.registration, reserved)}</td>
                          <td className="hidden px-3 py-3 sm:table-cell">{formatDate(c.expiration, reserved)}</td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              </div>

              <nav className="mt-6" aria-label="Results pages">
                <ul className="flex justify-center">
                  <li>
                    <button type="button" onClick={() => goTo(page - 1)} disabled={page === 1} className={`${pageBtn} rounded-l text-neutral-600 hover:bg-neutral-100`} aria-label="Previous page">
                      «
                    </button>
                  </li>
                  {pageLinks.map((p) => (
                    <li key={p}>
                      <button
                        type="button"
                        onClick={() => goTo(p)}
                        aria-current={p === page ? 'page' : undefined}
                        className={`${pageBtn} -ml-px ${p === page ? 'bg-neutral-600 text-white' : 'text-neutral-600 hover:bg-neutral-100'}`}
                      >
                        {p}
                      </button>
                    </li>
                  ))}
                  <li>
                    <button type="button" onClick={() => goTo(page + 1)} disabled={page >= pages} className={`${pageBtn} -ml-px rounded-r text-neutral-600 hover:bg-neutral-100`} aria-label="Next page">
                      »
                    </button>
                  </li>
                </ul>
                <p className="mt-4 text-center text-sm italic text-neutral-500">
                  {formatCount(result.count)}{result.capped ? '+' : ''} Results in {(result.timer / 1000).toFixed(3)} Seconds
                </p>
              </nav>
            </>
          )}

          {active && !loading && !result.search && (
            <div className="mt-10 text-center italic">
              <h2 className="not-italic">No Results</h2>
              <p className="mt-2">Please use Search bar and try again.</p>
            </div>
          )}

          <p className="mt-12 text-sm text-neutral-500">
            Search covers the 140 reserved colors, over a million publicly named colors, and every registered color.
          </p>
        </div>
      </Section>
    </>
  )
}
